'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { User, LoginCredentials, UserRole } from '@/types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

type UseAuth = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

export function useAuth(): UseAuth {
  const [state, setState] = useState<AuthState>(initialState);

  // CRITICAL: SSR-safe apiService initialization
  const apiServiceRef = useRef<any>(null);
  const [isApiServiceReady, setIsApiServiceReady] = useState(false);

  // Initialize apiService ONLY on client-side after mount
  useEffect(() => {
    if (typeof window !== 'undefined' && !apiServiceRef.current) {
      import('@/services/api').then((module) => {
        apiServiceRef.current = module.apiService;
        setIsApiServiceReady(true);
      }).catch((err) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to initialize apiService:', err);
        }
      });
    }
  }, []);

  const setUser = useCallback((user: User | null) => {
    setState(prev => ({
      ...prev,
      user,
      isAuthenticated: !!user,
      isLoading: false,
      error: null,
    }));
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({
      ...prev,
      isLoading,
    }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({
      ...prev,
      error,
      isLoading: false,
    }));
  }, []);

  const checkAuth = useCallback(async () => {
    // Guard: Don't check auth if apiService not ready yet
    // CRITICAL: Keep loading state true until apiService is ready
    if (!isApiServiceReady) {
      return;
    }

    try {
      setLoading(true);

      // SSR protection - skip auth check on server
      if (typeof window === 'undefined') {
        setUser(null);
        return;
      }

      // STANDARDIZED: Use standardized token key
      const storedUser = localStorage.getItem('auth_user');
      let token = localStorage.getItem('joyous_admin_token');

      // MIGRATION: Check old keys if new key not found
      if (!token) {
        token = localStorage.getItem('auth_token') ||
                localStorage.getItem('authToken') ||
                localStorage.getItem('accessToken');

        if (token) {
          // Migrate to new standardized key
          localStorage.setItem('joyous_admin_token', token);
        }
      }

      if (!token || !storedUser) {
        setUser(null);
        return;
      }

      try {
        const user = JSON.parse(storedUser);

        // Check when we last verified the token
        const lastVerification = localStorage.getItem('auth_last_verification');
        const now = Date.now();
        // With 90-day tokens, we only need to verify once per day at most
        const VERIFICATION_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

        // If we verified recently and have a user, skip verification
        if (lastVerification && (now - parseInt(lastVerification) < VERIFICATION_CACHE_DURATION)) {
          setUser(user);
          return;
        }

        // Verify token is still valid with lightweight verification
        const isValid = await apiServiceRef.current.verifyToken();

        if (isValid) {
          // Cache the verification timestamp
          localStorage.setItem('auth_last_verification', now.toString());
          setUser(user);
        } else {
          console.warn('⚠️ Token verification failed');
          // Token is invalid, clear auth
          apiServiceRef.current.clearAuth();
          localStorage.removeItem('auth_last_verification');
          setUser(null);
        }
      } catch (error: any) {
        console.error('Error during token verification:', error);

        // IMPORTANT: Only logout if it's a genuine auth error (401/403)
        // For network errors, server errors, or any other issue, KEEP USER LOGGED IN!
        const isAuthError = error?.response?.status === 401 || error?.response?.status === 403;

        if (isAuthError) {
          // Genuine auth error - logout
          apiServiceRef.current.clearAuth();
          localStorage.removeItem('auth_last_verification');
          setUser(null);
        } else {
          // Network error, server error, JSON parse error, or any other issue
          // KEEP USER LOGGED IN! Try to use cached user data
          try {
            const user = JSON.parse(storedUser);
            setUser(user);

            // Log warning but don't logout
            if (process.env.NODE_ENV === 'development') {
              console.warn('Auth check failed due to network/error, keeping user logged in:', error);
            }
          } catch (parseError) {
            // If even parsing fails, then clear auth
            apiServiceRef.current.clearAuth();
            localStorage.removeItem('auth_last_verification');
            setUser(null);
          }
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Auth check failed:', error);
      }
      setUser(null);
    } finally {
      // CRITICAL: Always clear loading state, regardless of success/failure
      setLoading(false);
    }
  }, [setUser, setLoading, isApiServiceReady]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      setLoading(true);
      setError(null);

      // Guard: Check if apiService is ready
      if (!isApiServiceReady) {
        throw new Error('API service not ready');
      }

      const response = await apiServiceRef.current.login(credentials);

      // apiService.login returns the user object directly
      if (response) {
        // Set verification timestamp on successful login (client-side only)
        if (typeof window !== 'undefined') {
          localStorage.setItem('auth_last_verification', Date.now().toString());
        }
        setUser(response);
      } else {
        throw new Error('Login failed: Invalid response');
      }
    } catch (error: any) {
      // Enhanced error logging for debugging (development only)
      if (process.env.NODE_ENV === 'development') {
        console.error('Raw login error:', error);
        console.error('Error type:', typeof error);
        console.error('Error constructor:', error?.constructor?.name);
        console.error('Error properties:', Object.keys(error || {}));
      }

      // Handle ApiError with user-friendly messages
      if (error?.userMessage) {
        setError(error.userMessage);
      } else if (error?.message) {
        setError(error.message);
      } else if (typeof error === 'string') {
        setError(error);
      } else {
        setError('Login failed. Please try again.');
      }

      // Log technical details for debugging (development only)
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error details:', {
          type: error?.type,
          userMessage: error?.userMessage,
          technicalMessage: error?.technicalMessage,
          statusCode: error?.statusCode,
          timestamp: error?.timestamp,
          fullError: error
        });
      }

      throw error;
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading, setError, isApiServiceReady]);

  const logout = useCallback(async () => {
    try {
      setLoading(true);

      // Guard: Check if apiService is ready
      if (!isApiServiceReady) {
        // Still clear local state even if API not ready
        if (typeof window !== 'undefined') {
          localStorage.removeItem('auth_last_verification');
        }
        setUser(null);
        return;
      }

      await apiServiceRef.current.logout();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', error);
      }
    } finally {
      // Clear verification cache on logout (client-side only)
      if (typeof window !== 'undefined') {
        localStorage.removeItem('auth_last_verification');
      }
      setUser(null);
    }
  }, [setUser, setLoading, isApiServiceReady]);

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  // Auto-refresh token before expiry (for 90-day admin tokens)
  const refreshIfNeeded = useCallback(async () => {
    // Guard: Check if apiService is ready
    if (!isApiServiceReady) {
      return false;
    }

    const token = localStorage.getItem('joyous_admin_token');
    if (!token) return false;

    try {
      // Decode JWT payload (client-side, no API call)
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      const payload = JSON.parse(atob(parts[1]));
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = (payload.exp || 0) - now;

      // Refresh if less than 1 day remaining
      const ONE_DAY = 24 * 60 * 60;
      if (timeUntilExpiry > 0 && timeUntilExpiry < ONE_DAY) {
        await apiServiceRef.current.refreshToken();
        localStorage.setItem('auth_last_verification', Date.now().toString());
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh check failed:', error);
      return false;
    }
  }, [isApiServiceReady]);

  // Check authentication status on mount
  useEffect(() => {
    // Only check auth when apiService is ready
    if (!isApiServiceReady) {
      return;
    }

    checkAuth();

    // Check every 6 hours if token refresh is needed (with 90-day tokens, no need to check frequently)
    const refreshInterval = setInterval(async () => {
      try {
        await refreshIfNeeded();
      } catch (error) {
        // Silently handle token refresh errors to prevent unhandled rejections
        console.error('Token refresh failed:', error);
      }
    }, 6 * 60 * 60 * 1000); // 6 hours

    return () => clearInterval(refreshInterval);
  }, [checkAuth, refreshIfNeeded, isApiServiceReady]);

  // Refresh token when tab becomes visible again (handles laptop sleep/wake)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && state.isAuthenticated) {
        // Check if token needs refresh when user comes back to the tab
        await refreshIfNeeded();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.isAuthenticated, refreshIfNeeded]);

  return {
    ...state,
    login,
    logout,
    checkAuth,
    clearError,
  };
}

// Helper hook for role-based access
export function useRoleAccess() {
  const { user, isAuthenticated } = useAuth();

  // FIX (2026-09-13, Issue #2 Phase 3): also accept the new RBAC
  // `primaryRoleSlug` (e.g. 'manager', 'support') surfaced by /auth/login.
  // Pure addition — legacy `user.role` check is preserved below.
  const effectiveRole: string | undefined =
    (user as any)?.primaryRoleSlug || user?.role;

  const hasRole = useCallback((role: UserRole): boolean => {
    if (!isAuthenticated || !user) return false;
    if (effectiveRole?.toLowerCase() === role.toLowerCase()) return true;
    // Legacy fallback for accounts with no primaryRoleSlug yet
    return user.role?.toLowerCase() === role.toLowerCase();
  }, [isAuthenticated, user, effectiveRole]);

  const hasAnyRole = useCallback((roles: UserRole[]): boolean => {
    if (!isAuthenticated || !user) return false;
    return roles.some(role => role.toLowerCase() === effectiveRole?.toLowerCase())
      || roles.some(role => role.toLowerCase() === user.role?.toLowerCase());
  }, [isAuthenticated, user, effectiveRole]);

  const canAccess = useCallback((requiredRoles: UserRole[] | undefined): boolean => {
    if (!isAuthenticated || !user) return false;

    // Admin has access to everything (case-insensitive, both legacy + RBAC slug)
    if (effectiveRole?.toLowerCase() === 'admin') return true;

    // If no roles are required, allow access
    if (!requiredRoles || requiredRoles.length === 0) return true;

    return requiredRoles.some(role => role.toLowerCase() === effectiveRole?.toLowerCase())
      || requiredRoles.some(role => role.toLowerCase() === user.role?.toLowerCase());
  }, [isAuthenticated, user, effectiveRole]);

  return {
    user,
    isAuthenticated,
    hasRole,
    hasAnyRole,
    canAccess,
    isAdmin: hasRole('admin'),
    isCustomer: hasRole('customer'),
    isDriver: hasRole('driver'),
  };
}